import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Coins, Package, TrendingUp, Ship, Landmark, AlertTriangle, Wallet,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import api from "../lib/api";
import { fmtMoney, fmtNum, pnlColor } from "../lib/format";
import KpiCard from "../components/KpiCard";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";

const COLORS = ["#ea580c", "#0d9488", "#2563eb", "#d97706", "#e11d48", "#7c3aed"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: kpi } = useQuery({ queryKey: ["exec"], queryFn: () => api.get("/dashboard/executive") });
  const { data: deals = [] } = useQuery({ queryKey: ["deals"], queryFn: () => api.get("/dashboard/deal-calculation") });
  const { data: exposure = [] } = useQuery({ queryKey: ["cp-exp"], queryFn: () => api.get("/counterparties-exposure") });

  if (!kpi) return <div className="text-slate-400">Loading dashboard…</div>;

  const topExposure = [...exposure].sort((a, b) => b.total_exposure - a.total_exposure).slice(0, 5);

  const cols = [
    { key: "trade_no", label: "Trade No.", render: (r) => <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">{r.trade_no}</span> },
    { key: "commodity", label: "Commodity", render: (r) => <span className="font-semibold uppercase text-slate-800 dark:text-slate-200">{r.commodity}</span> },
    { key: "quantity", label: "Qty (MT)", align: "right", render: (r) => fmtNum(r.quantity) },
    { key: "sales_contract_number", label: "Sale Contract", render: (r) => <span className="text-orange-600 dark:text-orange-400 font-medium">{r.sales_contract_number}</span> },
    { key: "sale_price", label: "Sale Price", align: "right", render: (r) => fmtMoney(r.sale_price) },
    { key: "total_sales_contract_value", label: "Sale Value", align: "right", render: (r) => fmtMoney(r.total_sales_contract_value) },
    { key: "purchase_contract_number", label: "Buy Contract" },
    { key: "total_purchase_contract_value", label: "Buy Value", align: "right", render: (r) => fmtMoney(r.total_purchase_contract_value) },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
  ];

  const expand = (r) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p className="text-xs text-slate-400">Trader / Buyer</p><p className="font-medium">{r.trader}</p></div>
      <div><p className="text-xs text-slate-400">LC Number</p><p className="font-mono">{r.lc_number}</p></div>
      <div><p className="text-xs text-slate-400">Buy Price</p><p className="font-mono">{fmtMoney(r.purchase_price)}</p></div>
      <div><p className="text-xs text-slate-400">Gross Margin</p><p className={`font-mono font-semibold ${pnlColor(r.total_sales_contract_value - r.total_purchase_contract_value)}`}>{fmtMoney(r.total_sales_contract_value - r.total_purchase_contract_value)}</p></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard testid="kpi-total-trades" label="Total Trades" value={kpi.total_trades} sub="Active contracts" color="teal" Icon={ClipboardList} onClick={() => navigate("/deals")} />
        <KpiCard testid="kpi-total-margin" label="Total Trade Margin" value={fmtMoney(kpi.total_margin)} sub="Physical gross margin" color="blue" Icon={Coins} />
        <KpiCard testid="kpi-total-volume" label="Total Volume" value={`${fmtNum(kpi.total_volume)} MT`} sub="Metric tonnes traded" color="orange" Icon={Package} />
        <KpiCard testid="kpi-sale-value" label="Total Sale Value" value={fmtMoney(kpi.total_sale_value)} sub="Across all trades" color="amber" Icon={Wallet} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard testid="kpi-net-pnl" label="Net P&L (All-in)" value={fmtMoney(kpi.total_pnl)} sub={`Realised ${fmtMoney(kpi.realised_pnl)}`} color={kpi.total_pnl >= 0 ? "emerald" : "rose"} Icon={TrendingUp} onClick={() => navigate("/positions")} />
        <KpiCard testid="kpi-unrealised" label="Unrealised Hedge P&L" value={fmtMoney(kpi.unrealised_pnl)} sub="Open F&O MTM" color="purple" Icon={TrendingUp} />
        <KpiCard testid="kpi-vessels" label="Active Vessels" value={kpi.active_vessels} sub={`${kpi.open_lcs} open LCs`} color="sky" Icon={Ship} onClick={() => navigate("/vessels")} />
        <KpiCard testid="kpi-risk" label="Risk & Overdue Alerts" value={kpi.risk_breaches + kpi.overdue_tasks} sub={`${kpi.risk_breaches} limit breaches · ${kpi.overdue_tasks} overdue`} color="rose" Icon={AlertTriangle} onClick={() => navigate("/positions")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-5">
          <h3 className="text-base font-bold mb-4">Sale Value by Commodity</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={kpi.by_commodity} margin={{ left: 10, right: 10 }}>
              <XAxis dataKey="commodity" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {kpi.by_commodity.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-5">
          <h3 className="text-base font-bold mb-4">Top Counterparty Exposure</h3>
          <div className="space-y-3">
            {topExposure.map((e, i) => (
              <div key={e.id} className="cursor-pointer" onClick={() => navigate("/counterparties")}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium truncate">{e.name}</span>
                  <span className="font-mono text-xs">{fmtMoney(e.total_exposure)}</span>
                </div>
                <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${e.breach ? "bg-rose-500" : "bg-orange-500"}`} style={{ width: `${Math.min(e.utilisation_pct, 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DataTable
        testid="dashboard-trade-table"
        title="Trade Details — click any row to expand"
        columns={cols}
        rows={deals}
        rowKey="sale_contract_id"
        csvName="trade_blotter"
        expandable={expand}
      />
    </div>
  );
}
